import { useMutation } from '@apollo/client';
import { Button, Text, Tray } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useProfile from '../../../lib/hooks/useProfile';
import { noop } from '../../../lib/utils';
import CountdownClock from '../CountdownClock';
import styles from './DeleteAccountTray.module.css';

const cx = classnames.bind(styles);

export const RESTORE_ACCOUNT_REQUEST = gql`
  mutation RestoreAccountRequest {
    restoreAccountRequest {
      success
    }
  }
`;

export default function DeleteAccountTray({ endDate, isProcessing, onRestoreAccount }) {
  const { t } = useTranslation('account');
  const [timerEnded, setTimerEnded] = useState(endDate && new Date().getTime() >= Date.parse(endDate));
  const [hasRequestedRestoration, setHasRequestedRestoration] = useState(false);
  const [restoreRequestTimeout, setRestoreRequestTimeout] = useState();
  const profile = useProfile();

  const [performRestoreRequest, { loading: restoreLoading }] = useMutation(RESTORE_ACCOUNT_REQUEST, {
    update(cache, { data }) {
      if (data?.restoreAccountRequest?.success) {
        setRestoreRequestTimeout(setTimeout(() => {
          cache.modify({
            id: cache.identify(profile),
            fields: {
              deletionStatus() {
                return {
                  endDate: null,
                  status: null,
                };
              },
            },
          });
        }, 5000));
      }
    },
    onCompleted: (data) => {
      if (data?.restoreAccountRequest?.success) {
        setHasRequestedRestoration(true);
      }
    },
  });

  useEffect(() => () => {
    if (restoreRequestTimeout) {
      clearTimeout(restoreRequestTimeout);
    }
  }, [restoreRequestTimeout]);

  const handleRestoreAccount = useCallback(() => {
    performRestoreRequest();
    onRestoreAccount();
  }, [onRestoreAccount, performRestoreRequest]);

  const handleTimerEnd = useCallback(() => {
    setTimerEnded(true);
  }, []);

  let trayText = (
    <Text as="p" className={cx('restore-text')}>
      {t('accountDeletionIn48Hours', { ns: 'account' })}
    </Text>
  );

  if (isProcessing || timerEnded) {
    trayText = (
      <Text as="p" className={cx('solitary-text')}>
        <Trans i18nKey="account:accountDeletionProcessing">
          Your account deletion request is currently being processed. This is not reversible.
          <br />
          If you have any questions or concerns, please contact our support team.
        </Trans>
      </Text>
    );
  } else if (hasRequestedRestoration) {
    trayText = (
      <Text as="p" className={cx('solitary-text')}>
        {t('accountRestorationProcessed', { ns: 'account' })}
        <br />
        {t('pageRefreshNotice', { ns: 'account' })}
      </Text>
    );
  }

  return (
    <Tray className={cx('delete-account-tray')} isOpen mobile={false}>
      <div className={cx('content-wrapper')}>
        {trayText}
        {!isProcessing && !timerEnded && !hasRequestedRestoration && (
          <Fragment>
            <CountdownClock
              endDate={endDate}
              labelled
              onEnd={handleTimerEnd}
            />
            <Button
              primary
              onClick={handleRestoreAccount}
              loading={restoreLoading}
            >
              {t('restoreMyAccount', { ns: 'account' })}
            </Button>
          </Fragment>
        )}
      </div>
    </Tray>
  );
}

DeleteAccountTray.propTypes = {
  endDate: PropTypes.string.isRequired,
  isProcessing: PropTypes.bool,
  onRestoreAccount: PropTypes.func,
};

DeleteAccountTray.defaultProps = {
  isProcessing: false,
  onRestoreAccount: noop,
};
